import { useLazyQuery, useQuery } from '@apollo/client';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import { useCallback, useEffect, useRef, useState } from 'react';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useConfig from '../../../lib/hooks/useConfig';
import { ANALYTICS_EVENTS } from '../../constants';
import BetaBadge from '../BetaBadge';
import EvFiltersPanel from '../EvFiltersPanel';
import EvLocationSearch from '../EvLocationSearch';
import EvMapView from '../EvMapView';
import EvStationPopup from '../EvStationPopup';
import EvStationList from '../EvStationList/EvStationList';
import Page from '../Page';
import styles from './EvSearchResultsPage.module.css';

const cx = classnames.bind(styles);

const EV_SEARCH_QUERY = gql`
  query EvStationsSearch(
    $latitude: Float!
    $longitude: Float!
    $radius: Float
    $networks: String
    $connectorTypes: String
    $chargingLevels: String
    $cardsAccepted: String
    $accessCode: String
    $limit: Int
  ) {
    evStationsNearby(
      latitude: $latitude
      longitude: $longitude
      radius: $radius
      networks: $networks
      connectorTypes: $connectorTypes
      chargingLevels: $chargingLevels
      cardsAccepted: $cardsAccepted
      accessCode: $accessCode
      limit: $limit
    ) {
      stations {
        id
        stationName
        streetAddress
        city
        state
        zip
        latitude
        longitude
        distanceMiles
        statusCode
        evNetwork
        evNetworkWeb
        evLevel1EvseNum
        evLevel2EvseNum
        evDcFastNum
        evPricing
        evJ1772ConnectorCount
        evJ1772PowerOutput
        evCcsConnectorCount
        evCcsPowerOutput
        evChademoConnectorCount
        evChademoPowerOutput
        evJ3400ConnectorCount
        evJ3400PowerOutput
        stationPhone
        accessDaysTime
        accessCode
        cardsAccepted
        dateLastConfirmed
      }
      total
      limit
    }
  }
`;

const EV_FILTERS_QUERY = gql`
  query EvFilters {
    evNetworks(limit: 100) {
      networks {
        id
        networkKey
        name
      }
    }
    evConnectorTypes {
      id
      code
      name
      isDc
    }
  }
`;

// Station fields fragment for reuse
const STATION_FIELDS = `
  id
  stationName
  streetAddress
  city
  state
  zip
  latitude
  longitude
  distanceMiles
  statusCode
  evNetwork
  evNetworkWeb
  evLevel1EvseNum
  evLevel2EvseNum
  evDcFastNum
  evPricing
  evJ1772ConnectorCount
  evJ1772PowerOutput
  evCcsConnectorCount
  evCcsPowerOutput
  evChademoConnectorCount
  evChademoPowerOutput
  evJ3400ConnectorCount
  evJ3400PowerOutput
  stationPhone
  accessDaysTime
  accessCode
  cardsAccepted
  dateLastConfirmed
`;

const EV_BOUNDS_QUERY = gql`
  query EvStationsByBounds(
    $northEastLat: Float!
    $northEastLng: Float!
    $southWestLat: Float!
    $southWestLng: Float!
    $networks: String
    $connectorTypes: String
    $chargingLevels: String
    $cardsAccepted: String
    $accessCode: String
    $limit: Int
  ) {
    evStationsByBounds(
      northEastLat: $northEastLat
      northEastLng: $northEastLng
      southWestLat: $southWestLat
      southWestLng: $southWestLng
      networks: $networks
      connectorTypes: $connectorTypes
      chargingLevels: $chargingLevels
      cardsAccepted: $cardsAccepted
      accessCode: $accessCode
      limit: $limit
    ) {
      stations {
        ${STATION_FIELDS}
      }
      total
      limit
    }
  }
`;

// Debounce delay for viewport changes
const VIEWPORT_DEBOUNCE_MS = 300;

// Default center: Geographic center of continental US
const US_CENTER = {
  lat: 39.8283,
  lng: -98.5795,
};

// Payment method UI value to API value mapping
// API values from ev-stations-serv: CREDIT, Debit, Cash, APPLE_PAY, ANDROID_PAY, FuelMan, M
const PAYMENT_API_VALUES = {
  pwgb: 'FuelMan,M',
  cash: 'Cash',
  debit: 'Debit',
  credit: 'CREDIT',
  apple_pay: 'APPLE_PAY',
  android_pay: 'ANDROID_PAY',
};

const ALL_PAYMENT_VALUES = Object.keys(PAYMENT_API_VALUES);

/**
 * Maps UI payment method values to API format
 * Returns undefined if all options selected (no filter needed)
 * @param {string[]} paymentMethods - Array of UI payment method values
 * @returns {string|undefined} - Comma-separated API values or undefined
 */
function mapPaymentMethodsToApi(paymentMethods) {
  // If all selected or empty, return undefined (no filter)
  if (!paymentMethods?.length || paymentMethods.length === ALL_PAYMENT_VALUES.length) {
    return undefined;
  }
  return paymentMethods
    .map(v => PAYMENT_API_VALUES[v])
    .filter(Boolean)
    .join(',');
}

/**
 * Maps UI access type values to API format
 * Returns undefined if no filter needed
 * @param {string[]} accessTypes - Array of access type values (e.g., ['public', 'private'])
 * @returns {string|undefined} - Comma-separated values or undefined
 */
function mapAccessTypesToApi(accessTypes) {
  if (!accessTypes?.length) {
    return undefined;
  }
  return accessTypes.join(',');
}

export default function EvSearchResultsPage() {
  const analytics = useAnalytics();
  const { mapboxMapKey } = useConfig();

  const [searchParams, setSearchParams] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [highlightedStationId, setHighlightedStationId] = useState(null);
  const [listSelectedStationId, setListSelectedStationId] = useState(null); // Track if selection came from list
  const [mapCenter, setMapCenter] = useState(US_CENTER);
  const [mapZoom, setMapZoom] = useState(4);
  const [filters, setFilters] = useState({
    chargerTypes: ['DCFast', 'Level2', 'Level1'], // Select All charger types by default
    connectorTypes: ['J1772', 'J1772COMBO', 'CHADEMO', 'TESLA'], // Select All connector types by default
    networks: [], // No network filter by default
    accessTypes: ['public'], // Public only by default
    paymentMethods: ['pwgb', 'cash', 'debit', 'credit', 'apple_pay', 'android_pay'], // Select All payment methods by default
  });
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false); // Start expanded on desktop
  const [locationDisplayName, setLocationDisplayName] = useState('');
  const [fitBoundsCounter, setFitBoundsCounter] = useState(0); // Increments only on location search to trigger fit
  const fitBoundsAppliedRef = useRef(0); // Tracks which counter value has been applied
  const [pendingLocationSearch, setPendingLocationSearch] = useState(false); // True when new location search is in progress
  const [pendingFilterChange, setPendingFilterChange] = useState(false); // True when filters changed, waiting for data
  const debounceTimeoutRef = useRef(null);
  const autoGeoTriggeredRef = useRef(false);
  const limit = 50;

  // Reverse geocode coordinates to get "City, State" format
  const reverseGeocode = useCallback(async (latitude, longitude) => {
    if (!mapboxMapKey) {
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?types=place&access_token=${mapboxMapKey}`,
      );
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const place = data.features[0];
        // Extract city and state from context
        const city = place.text;
        const stateContext = place.context?.find(c => c.id.startsWith('region'));
        const state = stateContext?.short_code?.replace('US-', '') || stateContext?.text || '';

        const displayName = state ? `${city}, ${state}` : city;
        setLocationDisplayName(displayName);
      }
    } catch (error) {
      // Keep showing "Current Location" if reverse geocoding fails
      // eslint-disable-next-line no-console
      console.warn('Reverse geocoding failed:', error);
    }
  }, [mapboxMapKey]);

  // Auto-detect user location on page load
  useEffect(() => {
    // Only run once on mount, and only on client side
    if (autoGeoTriggeredRef.current || typeof window === 'undefined') {
      return;
    }
    autoGeoTriggeredRef.current = true;

    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        // Set initial display as "Current Location" while we reverse geocode
        setLocationDisplayName('Current Location');

        // Trigger search with current location
        setSearchParams({
          latitude,
          longitude,
          radius: 25,
        });
        setMapCenter({ lat: latitude, lng: longitude });
        setMapZoom(11);
        setFitBoundsCounter(prev => prev + 1); // Trigger fitBounds for auto-detected location

        // Track auto-geolocation usage
        analytics.tagEvent({
          name: ANALYTICS_EVENTS.EV_CURRENT_LOCATION_USED,
          attributes: {
            source: 'auto',
            latitude,
            longitude,
          },
        });

        // Reverse geocode to get "City, State" display
        reverseGeocode(latitude, longitude);
      },
      () => {
        // Geolocation denied or failed - keep default US map view
        // No action needed, user can manually search
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      },
    );
  }, [analytics, reverseGeocode]);

  // Track page view on mount
  useEffect(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_MAP_VIEWED,
      attributes: {
        source: 'direct',
      },
    });
  }, [analytics]);

  // Cleanup debounce timeout on unmount
  useEffect(() => () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  }, []);

  // Fetch filter options
  const { data: filtersData } = useQuery(EV_FILTERS_QUERY);

  // Fetch stations when search is performed (radius-based)
  const { data: stationsData, loading: isLoadingStations, error: stationsError } = useQuery(
    EV_SEARCH_QUERY,
    {
      variables: searchParams ? {
        latitude: searchParams.latitude,
        longitude: searchParams.longitude,
        radius: searchParams.radius,
        networks: filters.networks?.join(',') || undefined,
        connectorTypes: filters.connectorTypes?.join(',') || undefined,
        chargingLevels: filters.chargerTypes?.join(',') || undefined,
        cardsAccepted: mapPaymentMethodsToApi(filters.paymentMethods),
        accessCode: mapAccessTypesToApi(filters.accessTypes),
        limit,
      } : undefined,
      skip: !searchParams,
    },
  );

  // Fetch stations by viewport bounds (pan/zoom)
  const [fetchByBounds, { data: boundsData, loading: isLoadingBounds, error: boundsError }] = useLazyQuery(
    EV_BOUNDS_QUERY,
    { fetchPolicy: 'cache-and-network' },
  );

  const handleLocationSelect = useCallback((location) => {
    const { latitude, longitude, displayName } = location;

    // Mark that we're starting a new location search - this will prevent stale bounds data from showing
    setPendingLocationSearch(true);

    setSearchParams({
      latitude,
      longitude,
      radius: 25,
    });
    setMapCenter({ lat: latitude, lng: longitude });
    setMapZoom(11);
    setLocationDisplayName(displayName); // Update display name for input
    setFitBoundsCounter(prev => prev + 1); // Trigger fitBounds on new location search

    // Track with appropriate event based on source
    const eventName = displayName === 'Current Location'
      ? ANALYTICS_EVENTS.EV_CURRENT_LOCATION_USED
      : ANALYTICS_EVENTS.EV_LOCATION_SEARCHED;

    analytics.tagEvent({
      name: eventName,
      attributes: {
        query: displayName,
        latitude,
        longitude,
      },
    });
  }, [analytics]);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    // Mark filter change pending - use radius stations until bounds query refreshes
    setPendingFilterChange(true);

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_FILTER_CHANGED,
      attributes: {
        chargerTypes: newFilters.chargerTypes?.join(',') || '',
        connectorTypes: newFilters.connectorTypes?.join(',') || '',
        networks: newFilters.networks?.join(',') || '',
        accessTypes: newFilters.accessTypes?.join(',') || '',
      },
    });
  }, [analytics]);

  const handleFiltersReset = useCallback(() => {
    setFilters({});
    // Mark filter change pending - use radius stations until bounds query refreshes
    setPendingFilterChange(true);

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_FILTERS_RESET,
    });
  }, [analytics]);

  const handleClearLocation = useCallback(() => {
    setSearchParams(null);
    setLocationDisplayName('');
    // Note: fitBoundsCounter is NOT incremented - keep current map view

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_LOCATION_CLEARED,
    });
  }, [analytics]);

  const handleToggleFilters = useCallback(() => {
    setIsFiltersCollapsed(prev => !prev);
  }, []);

  const handleStationSelect = useCallback((station) => {
    setSelectedStation(station);
    setListSelectedStationId(null); // Clear list selection on map click

    // Determine charging level
    let chargingLevel = 'Level 1';
    if (station.evDcFastNum > 0) {
      chargingLevel = 'DC Fast';
    } else if (station.evLevel2EvseNum > 0) {
      chargingLevel = 'Level 2';
    }

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_MARKER_CLICKED,
      attributes: {
        stationId: station.id,
        stationName: station.stationName,
        network: station.evNetwork || 'Unknown',
        chargingLevel,
      },
    });
  }, [analytics]);

  const handleStationHover = useCallback((station) => {
    setHighlightedStationId(station?.id || null);
  }, []);

  const handleListItemClick = useCallback((station, index) => {
    setSelectedStation(station);
    setListSelectedStationId(station.id); // Track that selection came from list
    // Note: Map centering is now handled by EvMapView when selectedStation changes

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_LIST_ITEM_CLICKED,
      attributes: {
        stationId: station.id,
        position: index,
      },
    });
  }, [analytics]);

  const handleMapMove = useCallback((e) => {
    if (e?.bounds) {
      // Clear any pending debounce
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Debounce to avoid excessive API calls on every small movement
      debounceTimeoutRef.current = setTimeout(() => {
        // Track map panning (debounced to avoid spam)
        analytics.tagEvent({
          name: ANALYTICS_EVENTS.EV_MAP_PANNED,
          attributes: {
            newCenter: `${e.center.lat.toFixed(4)}, ${e.center.lng.toFixed(4)}`,
          },
        });

        const { northEast, southWest } = e.bounds;
        if (northEast && southWest) {
          // Auto-fetch viewport stations on pan/zoom
          fetchByBounds({
            variables: {
              northEastLat: northEast.lat,
              northEastLng: northEast.lng,
              southWestLat: southWest.lat,
              southWestLng: southWest.lng,
              networks: filters.networks?.join(',') || undefined,
              connectorTypes: filters.connectorTypes?.join(',') || undefined,
              chargingLevels: filters.chargerTypes?.join(',') || undefined,
              cardsAccepted: mapPaymentMethodsToApi(filters.paymentMethods),
              accessCode: mapAccessTypesToApi(filters.accessTypes),
              limit: 200,
            },
          }).then(() => {
            // Clear pending flag after bounds data is fetched for new location
            setPendingLocationSearch(false);
          });
        }
      }, VIEWPORT_DEBOUNCE_MS);
    }
  }, [analytics, fetchByBounds, filters]);

  const handleZoomChange = useCallback((newZoom) => {
    setMapZoom(newZoom);
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_MAP_ZOOMED,
      attributes: {
        zoomLevel: newZoom,
      },
    });
  }, [analytics]);

  // Fetch initial viewport stations when map loads
  const handleMapLoad = useCallback((e) => {
    if (e?.bounds) {
      const { northEast, southWest } = e.bounds;
      fetchByBounds({
        variables: {
          northEastLat: northEast.lat,
          northEastLng: northEast.lng,
          southWestLat: southWest.lat,
          southWestLng: southWest.lng,
          networks: filters.networks?.join(',') || undefined,
          connectorTypes: filters.connectorTypes?.join(',') || undefined,
          chargingLevels: filters.chargerTypes?.join(',') || undefined,
          cardsAccepted: mapPaymentMethodsToApi(filters.paymentMethods),
          accessCode: mapAccessTypesToApi(filters.accessTypes),
          limit: 200,
        },
      });
    }
  }, [fetchByBounds, filters]);

  // Separate station data for map vs list
  // Map shows viewport stations (from bounds query) - fetched on load and pan/zoom
  // List shows radius stations (from search query) - fetched when location entered
  const radiusStations = stationsData?.evStationsNearby?.stations || [];
  const viewportStations = boundsData?.evStationsByBounds?.stations || [];

  // Clear pending filter change flag when radius query completes with data
  useEffect(() => {
    if (pendingFilterChange && !isLoadingStations && radiusStations.length >= 0) {
      setPendingFilterChange(false);
    }
  }, [pendingFilterChange, isLoadingStations, radiusStations.length]);

  // Total reflects radius search results (for list)
  const total = stationsData?.evStationsNearby?.total || radiusStations.length;
  const hasMore = false; // Pagination removed - all results returned at once
  const isLoading = isLoadingStations || isLoadingBounds;
  const hasError = !!(stationsError || boundsError);

  // Filter data for EvFiltersPanel
  const networks = filtersData?.evNetworks?.networks || [];

  // Stations displayed on map
  // Use radius stations when:
  // - A new location search is pending (avoid stale bounds data)
  // - A filter change is pending (avoid stale bounds data with old filters)
  // Once bounds query completes, switch to viewport stations
  const useViewportStations = !pendingLocationSearch && !pendingFilterChange && viewportStations.length > 0;
  const mapStations = useViewportStations ? viewportStations : radiusStations;

  // Determine if fitBounds should be triggered (only when counter increases AND data is available)
  const shouldFitBounds = fitBoundsCounter > fitBoundsAppliedRef.current && mapStations.length > 0;

  // Mark fitBounds as applied after render so it only triggers once per location search
  useEffect(() => {
    if (shouldFitBounds) {
      // Update ref after a short delay to ensure MapBoxMap has processed the fitBounds
      const timer = setTimeout(() => {
        fitBoundsAppliedRef.current = fitBoundsCounter;
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldFitBounds, fitBoundsCounter]);

  // Close popup if selected station is no longer in filtered results
  useEffect(() => {
    if (selectedStation && mapStations.length > 0) {
      const stationStillExists = mapStations.some(s => s.id === selectedStation.id);
      if (!stationStillExists) {
        setSelectedStation(null);
      }
    }
  }, [selectedStation, mapStations]);

  // Track when detail panel is opened
  useEffect(() => {
    if (selectedStation) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.EV_DETAIL_VIEWED,
        attributes: {
          stationId: selectedStation.id,
          stationName: selectedStation.stationName,
          network: selectedStation.evNetwork || 'Unknown',
        },
      });
    }
  }, [selectedStation, analytics]);

  const handleCloseDetailTray = useCallback(() => {
    setSelectedStation(null);
  }, []);

  const handleDirectionsClick = useCallback((station) => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_DIRECTIONS_CLICKED,
      attributes: {
        stationId: station.id,
        stationName: station.stationName,
      },
    });
  }, [analytics]);

  const handlePhoneClick = useCallback((station) => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_PHONE_CLICKED,
      attributes: {
        stationId: station.id,
        stationName: station.stationName,
      },
    });
  }, [analytics]);

  const handleWebsiteClick = useCallback((station) => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.EV_WEBSITE_CLICKED,
      attributes: {
        stationId: station.id,
        stationName: station.stationName,
        network: station.evNetwork || 'Unknown',
        url: station.evNetworkWeb,
      },
    });
  }, [analytics]);

  // Render list content based on state
  const renderListContent = () => {
    if (!searchParams) {
      return (
        <div className={cx('emptyState')}>
          <p>Search for a location to see stations in the list.</p>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className={cx('errorState')}>
          <p>Unable to load stations. Please try again.</p>
        </div>
      );
    }

    return (
      <EvStationList
        stations={radiusStations}
        isLoading={isLoadingStations}
        hasMore={hasMore}
        onStationClick={handleListItemClick}
        selectedStationId={selectedStation?.id}
        highlightedStationId={highlightedStationId}
        onStationHover={handleStationHover}
      />
    );
  };

  return (
    <Page title="Find EV Charging Stations" compact={false}>
      <div className={cx('page')}>
        {/* Header */}
        <header className={cx('header')}>
          <div className={cx('headerContent')}>
            <div className={cx('titleRow')}>
              <img
                src="/assets-v2/svg/chargebuddy-logo.svg"
                alt="ChargeBuddy - Find EV Charging"
                className={cx('logo')}
              />
              <BetaBadge />
            </div>

            <EvLocationSearch
              onLocationSelect={handleLocationSelect}
              onClearLocation={handleClearLocation}
              hasLocation={!!searchParams}
              isLoading={isLoading}
              placeholder="Enter city or ZIP code"
              initialValue={locationDisplayName}
            />
          </div>
        </header>

        {/* Main Content */}
        <div className={cx('mainContent')}>
          {/* Station List Panel (Left) */}
          <aside className={cx('listPanel')}>
            <div className={cx('listHeader')}>
              <div className={cx('legend')}>
                <div className={cx('legendItem')}>
                  <span className={cx('legendDot', 'legendDotDcFast')} />
                  <span>DC Fast</span>
                </div>
                <div className={cx('legendItem')}>
                  <span className={cx('legendDot', 'legendDotLevel2')} />
                  <span>Level 2</span>
                </div>
                <div className={cx('legendItem')}>
                  <span className={cx('legendDot', 'legendDotLevel1')} />
                  <span>Level 1</span>
                </div>
              </div>
              {searchParams && (
                <p className={cx('resultsCount')}>
                  {total} stations found
                </p>
              )}
            </div>

            {/* Horizontal layout: filters on left, results on right */}
            <div className={cx('filtersAndResults')}>
              <div className={cx('filtersColumn')}>
                <EvFiltersPanel
                  networks={networks}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onReset={handleFiltersReset}
                  isCollapsed={isFiltersCollapsed}
                  onToggleCollapse={handleToggleFilters}
                />
              </div>
              <div className={cx('resultsColumn')}>
                <div className={cx('listContent')}>
                  {renderListContent()}
                </div>
              </div>
            </div>
          </aside>

          {/* Map View (Right) */}
          <main className={cx('mapPanel')}>
            <EvMapView
              accessToken={mapboxMapKey}
              stations={mapStations}
              selectedStation={selectedStation}
              selectedStationId={selectedStation?.id}
              highlightedStationId={highlightedStationId}
              listSelectedStationId={listSelectedStationId}
              center={mapCenter}
              zoom={mapZoom}
              onStationSelect={handleStationSelect}
              onStationHover={handleStationHover}
              onMapMove={handleMapMove}
              onMapLoad={handleMapLoad}
              onZoomChange={handleZoomChange}
              fitBounds={shouldFitBounds}
              renderPopup={station => (
                <EvStationPopup
                  station={station}
                  onClose={handleCloseDetailTray}
                  onDirectionsClick={handleDirectionsClick}
                  onPhoneClick={handlePhoneClick}
                  onWebsiteClick={handleWebsiteClick}
                />
              )}
            />
          </main>
        </div>
      </div>
    </Page>
  );
}
